import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  #handleCartAdd = (event) => {
    if (event.detail?.data?.source === 'ask-seyune') return;
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    requestAnimationFrame(() => this.#updateStickyState());
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const addOns = dialog.querySelector('seyune-cart-add-ons');
    if (addOns instanceof SeyuneCartAddOns) addOns.upgrade();

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

/**
 * Ranks and adds cart-drawer products that unlock free shipping.
 * Slot one favors the strongest complementary/bestseller signal.
 * Slot two favors the lowest qualifying price while keeping type diversity when possible.
 */
class SeyuneCartAddOns extends HTMLElement {
  /** @type {HTMLButtonElement | null} */
  #pendingButton = null;
  /** @type {number} */
  #upgradeFrame = 0;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.#upgradeFrame = requestAnimationFrame(() => {
      if (this.isConnected) this.upgrade();
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    cancelAnimationFrame(this.#upgradeFrame);
  }

  upgrade() {
    this.#upgradeRecommendations();
  }

  #normalizeType(value) {
    return typeof value === 'string' ? value.trim().toLowerCase().replace(/\s+/g, ' ') : '';
  }

  #productDomain(value) {
    const type = this.#normalizeType(value);
    if (!type) return '';

    if (/hair|shampoo|conditioner|scalp|styling|hair dye/.test(type)) return 'haircare';
    if (/body|bath|shower|hand|foot|deodorant/.test(type)) return 'bath-body';
    if (/makeup|foundation|cushion|concealer|mascara|eyeliner|eyeshadow|brow|lip|blush|bronzer|contour|powder|primer|setting spray/.test(type)) {
      return 'makeup';
    }
    if (/skin|serum|toner|ampoule|essence|cream|moistur|cleanser|cleansing|sunscreen|sun care|mask|pad|acne|peel|exfoliant/.test(type)) {
      return 'skincare';
    }

    return '';
  }

  #normalizeCandidate(rawCandidate) {
    if (!rawCandidate || typeof rawCandidate !== 'object') return null;

    const productId = String(rawCandidate.productId || '');
    const variantId = String(rawCandidate.variantId || '');
    const title = typeof rawCandidate.title === 'string' ? rawCandidate.title : '';
    const url = typeof rawCandidate.url === 'string' ? rawCandidate.url : '';
    const imageUrl = typeof rawCandidate.imageUrl === 'string' ? rawCandidate.imageUrl : '';
    const imageAlt = typeof rawCandidate.imageAlt === 'string' ? rawCandidate.imageAlt : title;
    const formattedPrice = typeof rawCandidate.formattedPrice === 'string' ? rawCandidate.formattedPrice : '';
    const type = this.#normalizeType(rawCandidate.type);
    const sourceType = this.#normalizeType(rawCandidate.sourceType);
    const price = Number(rawCandidate.price);
    const inventory = Number(rawCandidate.inventory);
    const rank = Number(rawCandidate.rank);

    if (!productId || !variantId || !title || !url || !imageUrl || !formattedPrice || !type) return null;
    if (!Number.isFinite(price) || !Number.isFinite(inventory)) return null;

    return {
      productId,
      variantId,
      title,
      url,
      imageUrl,
      imageAlt,
      formattedPrice,
      type,
      sourceType,
      price,
      inventory,
      available: rawCandidate.available === true,
      published: rawCandidate.published === true,
      giftCard: rawCandidate.giftCard === true,
      rank: Number.isFinite(rank) ? rank : Number.MAX_SAFE_INTEGER,
      complementaryRank: Number.MAX_SAFE_INTEGER,
      fallbackRank: Number.MAX_SAFE_INTEGER,
      isComplementary: false,
    };
  }

  #compareProductIds(firstId, secondId) {
    if (firstId.length !== secondId.length) return firstId.length - secondId.length;
    if (firstId === secondId) return 0;
    return firstId < secondId ? -1 : 1;
  }

  #selectRecommendations(payload) {
    if (!payload || typeof payload !== 'object') return null;
    if (!Array.isArray(payload.cartProductIds) || !Array.isArray(payload.cartTypes)) return null;
    if (!Array.isArray(payload.complementary) || !Array.isArray(payload.fallback)) return null;

    const gap = Number(payload.gap);
    if (!Number.isFinite(gap) || gap <= 0) return null;

    const cartProductIds = new Set(payload.cartProductIds.map((id) => String(id)));
    const cartTypes = new Set(payload.cartTypes.map((type) => this.#normalizeType(type)).filter(Boolean));
    const cartDomains = new Set(payload.cartTypes.map((type) => this.#productDomain(type)).filter(Boolean));
    const bandExtra = gap <= 1000 ? 800 : gap <= 2500 ? 1500 : 2500;
    const bandCeiling = gap + bandExtra;

    const isEligible = (candidate) =>
      candidate !== null &&
      candidate.available &&
      candidate.published &&
      candidate.inventory > 0 &&
      candidate.price >= gap &&
      !candidate.giftCard &&
      candidate.type !== 'gift card' &&
      !cartProductIds.has(candidate.productId) &&
      !cartTypes.has(candidate.type);
    const matchesCartDomain = (candidate) => {
      if (!cartDomains.size) return true;
      const candidateDomain = this.#productDomain(candidate.type);
      return Boolean(candidateDomain && cartDomains.has(candidateDomain));
    };

    const complementaryById = new Map();
    for (const rawCandidate of payload.complementary) {
      const candidate = this.#normalizeCandidate(rawCandidate);
      if (!isEligible(candidate) || !matchesCartDomain(candidate) || !candidate.sourceType) continue;

      candidate.isComplementary = true;
      candidate.complementaryRank = candidate.rank;
      const existing = complementaryById.get(candidate.productId);
      if (!existing) {
        complementaryById.set(candidate.productId, candidate);
      } else {
        existing.complementaryRank = Math.min(existing.complementaryRank, candidate.complementaryRank);
      }
    }

    const fallbackById = new Map();
    for (const rawCandidate of payload.fallback) {
      const candidate = this.#normalizeCandidate(rawCandidate);
      if (!isEligible(candidate) || !matchesCartDomain(candidate)) continue;

      candidate.fallbackRank = candidate.rank;
      const existing = fallbackById.get(candidate.productId);
      if (!existing) {
        fallbackById.set(candidate.productId, candidate);
      } else {
        existing.fallbackRank = Math.min(existing.fallbackRank, candidate.fallbackRank);
      }
    }

    const allById = new Map();
    for (const candidate of fallbackById.values()) allById.set(candidate.productId, candidate);
    for (const candidate of complementaryById.values()) {
      const fallbackCandidate = allById.get(candidate.productId);
      if (fallbackCandidate) candidate.fallbackRank = fallbackCandidate.fallbackRank;
      allById.set(candidate.productId, candidate);
    }

    const inBand = (candidate) => candidate.price <= bandCeiling;
    const byBestMatch = (first, second) =>
      first.complementaryRank - second.complementaryRank ||
      first.fallbackRank - second.fallbackRank ||
      first.price - gap - (second.price - gap) ||
      this.#compareProductIds(first.productId, second.productId);
    const byPurchaseLikelihood = (first, second) =>
      first.fallbackRank - second.fallbackRank ||
      first.price - gap - (second.price - gap) ||
      this.#compareProductIds(first.productId, second.productId);
    const byLowestPrice = (first, second) =>
      first.price - second.price ||
      Number(second.isComplementary) - Number(first.isComplementary) ||
      first.complementaryRank - second.complementaryRank ||
      first.fallbackRank - second.fallbackRank ||
      this.#compareProductIds(first.productId, second.productId);

    const selected = [];
    const selectedIds = new Set();
    const selectedTypes = new Set();
    const addFirst = (candidates, preferNewType = false) => {
      const available = candidates.filter((candidate) => !selectedIds.has(candidate.productId));
      const candidate =
        (preferNewType && available.find((item) => !selectedTypes.has(item.type))) || available[0] || null;
      if (!candidate) return false;
      selected.push(candidate);
      selectedIds.add(candidate.productId);
      selectedTypes.add(candidate.type);
      return true;
    };

    const complementaryInBand = Array.from(complementaryById.values()).filter(inBand).sort(byBestMatch);
    const fallbackInBand = Array.from(fallbackById.values()).filter(inBand).sort(byPurchaseLikelihood);
    const allInBandByPrice = Array.from(allById.values()).filter(inBand).sort(byLowestPrice);

    // Slot one: strongest product-to-cart match; use bestseller order when complementary data is absent.
    if (!addFirst(complementaryInBand)) addFirst(fallbackInBand);

    // Slot two: lowest-price item that still unlocks shipping; prefer a different product type.
    if (selected.length < 2) addFirst(allInBandByPrice, true);

    // Safety valve: relax only the price-band ceiling, never stock, publication, cart, or gap coverage.
    if (selected.length < 2) {
      const aboveBand = Array.from(allById.values())
        .filter((candidate) => candidate.price > bandCeiling)
        .sort(byLowestPrice);
      addFirst(aboveBand, true);
    }

    return selected;
  }

  #createCard(candidate) {
    const card = document.createElement('article');
    card.className = 'seyune-fsa__card';

    const media = document.createElement('a');
    media.className = 'seyune-fsa__media';
    media.href = candidate.url;
    media.setAttribute('aria-label', candidate.title);

    const image = document.createElement('img');
    image.className = 'seyune-fsa__image';
    image.src = candidate.imageUrl;
    image.alt = candidate.imageAlt;
    image.loading = 'lazy';
    image.sizes = '92px';
    media.append(image);

    const info = document.createElement('div');
    info.className = 'seyune-fsa__info';

    const title = document.createElement('a');
    title.className = 'seyune-fsa__title';
    title.href = candidate.url;
    title.textContent = candidate.title;

    const price = document.createElement('span');
    price.className = 'seyune-fsa__price';
    price.textContent = candidate.formattedPrice;
    info.append(title, price);

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'seyune-fsa__button button button--secondary';
    button.dataset.seyuneAddOnAdd = '';
    button.dataset.variantId = candidate.variantId;
    button.textContent = 'Add';

    card.append(media, info, button);
    return card;
  }

  #upgradeRecommendations() {
    const payloadElement = this.querySelector('script[data-seyune-addons-payload]');
    const grid = this.querySelector('.seyune-fsa__grid');
    if (!(payloadElement instanceof HTMLScriptElement) || !(grid instanceof HTMLElement)) return;

    try {
      const payload = JSON.parse(payloadElement.textContent || '');
      const recommendations = this.#selectRecommendations(payload);
      if (recommendations === null) return;

      if (recommendations.length === 0) {
        this.classList.add('hidden');
        return;
      }

      const cards = document.createDocumentFragment();
      for (const recommendation of recommendations) cards.append(this.#createCard(recommendation));
      if (cards.childElementCount !== recommendations.length) return;

      grid.replaceChildren(cards);
      this.classList.remove('hidden');
    } catch (error) {
      console.error('Unable to upgrade free-shipping add-ons', error);
    }
  }

  #handleClick = async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('button[data-seyune-add-on-add]');
    if (!(button instanceof HTMLButtonElement) || this.#pendingButton) return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    this.#pendingButton = button;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    const sectionsToUpdate = new Set();
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionsToUpdate.add(item.dataset.sectionId);
    });

    if (sectionsToUpdate.size) {
      formData.append('sections', Array.from(sectionsToUpdate).join(','));
      formData.append('sections_url', window.location.pathname);
    }

    const fetchCfg = fetchConfig('javascript', { body: formData });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: { ...fetchCfg.headers, Accept: 'text/html' },
      });
      const data = await response.json();
      if (data.status) throw new Error(data.description || data.message || 'Unable to add item to cart');

      this.dispatchEvent(
        new CartAddEvent(data, variantId, {
          source: 'seyune-cart-add-ons',
          itemCount: 1,
          variantId,
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      if (button.isConnected) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
      this.#pendingButton = null;
    }
  };
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

if (!customElements.get('seyune-cart-add-ons')) {
  customElements.define('seyune-cart-add-ons', SeyuneCartAddOns);
}
